import { Key } from 'react'

import { makeStyles } from '@material-ui/core/styles'
import {
  Table,
  TableBody,
  TableCell,
  TableSortLabel,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core'

import { map } from 'lodash'
import { CSSProperties } from 'react'

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  container: {
    maxHeight: 900,
  },
})

type ColumnDefinition<R> = {
  id: string
  label: string
  render: (record: R) => React.ReactNode
  styles?: CSSProperties
  align?: 'left' | 'right' | 'center'
  sortKey?: string
  hidden?: boolean
}

export type SortDirection = 'desc' | 'asc'

type KeyableFields<T> = keyof {
  [Property in keyof T as Exclude<Property, T[Property] extends Key ? never : Property>]: Key
}

export type DataTableProps<R> = {
  total: number
  records: R[]
  columns: ColumnDefinition<R>[]
  onSortChange: (key: string, dir: SortDirection) => void
  onPageChange: (pageNum: number) => void
  onPageSizeChange: (pageSize: number) => void
  order: SortDirection
  orderBy: string // could try ref dynamic column.label,
  rowsPerPage: number
  page: number
  keyBy: keyof R & KeyableFields<R>
}

function DataTable<R>({
  total,
  records,
  columns,
  onSortChange,
  onPageChange,
  onPageSizeChange,
  order,
  orderBy,
  rowsPerPage,
  page,
  keyBy,
}: DataTableProps<R>) {
  const classes = useStyles()
  return (
    <div className={classes.root}>
      <TableContainer className={classes.container}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) =>
                column.hidden ? null : (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ ...column.styles }}
                  >
                    {column.sortKey ? (
                      <TableSortLabel
                        active={orderBy === column.sortKey}
                        direction={orderBy === column.sortKey ? order : 'asc'}
                        onClick={() => {
                          const isAsc = orderBy === column.sortKey && order === 'asc'
                          onSortChange(column.sortKey!, isAsc ? 'desc' : 'asc')
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {map(records, (record) => (
              <TableRow
                tabIndex={-1}
                key={`${record[keyBy]}`}
              >
                {/* key-lean up */}
                {map(columns, (column) =>
                  column.hidden ? null : (
                    <TableCell
                      key={column.id}
                      align={column.align}
                    >
                      {column.render(record)}
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onPageSizeChange(+event.target.value)}
      />
    </div>
  )
}
export default DataTable
